const PENDING_INVITE_KEY = "lms_pending_invite";

/** SEC-8: a remembered invite is only good for a day — after that, open the link again. */
export const PENDING_INVITE_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * An invite opened while signed out is remembered here, so the person can sign in or create an
 * account first and still land on the redeem page afterwards. localStorage rather than
 * sessionStorage: the email-verification link may open in a new tab.
 *
 * SEC-8: it is a short hand-off, not a standing grant. It expires after 24 hours, is dropped on
 * any sign-out, and is consumed by the next sign-in whatever the tenant — so the next person to
 * use this browser does not inherit it. N13: and the redeem page never redeems it silently — the
 * signed-in person confirms "Accept this invite with this account?" first.
 */
export function rememberPendingInvite(tenantSlug: string, token: string): void {
  try {
    localStorage.setItem(
      PENDING_INVITE_KEY,
      JSON.stringify({ tenantSlug, token, savedAt: Date.now() }),
    );
  } catch {
    // Storage blocked: the person can still open the invite link again after signing in.
  }
}

/** Drops any remembered invite (sign-out). */
export function forgetPendingInvite(): void {
  try {
    localStorage.removeItem(PENDING_INVITE_KEY);
  } catch {
    // ignore
  }
}

/** The redeem path for a pending invite in this tenant. Always clears what was remembered. */
export function takePendingInvitePath(tenantSlug: string): string | null {
  try {
    const raw = localStorage.getItem(PENDING_INVITE_KEY);
    if (!raw) return null;
    localStorage.removeItem(PENDING_INVITE_KEY);
    const pending = JSON.parse(raw) as { tenantSlug?: string; token?: string; savedAt?: number };
    if (pending.tenantSlug !== tenantSlug || !pending.token) return null;
    if (
      typeof pending.savedAt !== "number" ||
      Date.now() - pending.savedAt > PENDING_INVITE_TTL_MS
    ) {
      return null;
    }
    return `/t/${tenantSlug}/invite/${encodeURIComponent(pending.token)}`;
  } catch {
    return null;
  }
}

/**
 * §31: the pending invite's token in this tenant, left in place (registration sends it so a new
 * staff member can join an approved-only school; the redeem step after sign-in still consumes it).
 */
export function peekPendingInviteToken(tenantSlug: string): string | null {
  try {
    const raw = localStorage.getItem(PENDING_INVITE_KEY);
    if (!raw) return null;
    const pending = JSON.parse(raw) as { tenantSlug?: string; token?: string; savedAt?: number };
    if (pending.tenantSlug !== tenantSlug || !pending.token) return null;
    if (
      typeof pending.savedAt !== "number" ||
      Date.now() - pending.savedAt > PENDING_INVITE_TTL_MS
    ) {
      return null;
    }
    return pending.token;
  } catch {
    return null;
  }
}
